'use client'

import { useEffect, useRef } from 'react'

interface UserIdProviderProps {
  /**
   * Function that returns the current user ID
   * This will be called to get the user ID for feature checks and payments
   */
  getUserId: () => string | null | Promise<string | null>

  /**
   * Dependencies array - when these change, the userId will be re-fetched
   */
  deps?: any[]

  /**
   * Callback when userId changes
   */
  onUserIdChange?: (userId: string | null) => void
}

/**
 * UserIdProvider - Bridge component for auth integration
 *
 * This component allows you to integrate any auth provider with UseCelo.
 * Simply provide a getUserId function that returns the current user's ID.
 *
 * @example
 * // With Clerk
 * import { useUser } from '@clerk/nextjs'
 *
 * function ClerkUserIdProvider() {
 *   const { user } = useUser()
 *   return (
 *     <UserIdProvider
 *       getUserId={() => user?.id || null}
 *       deps={[user?.id]}
 *     />
 *   )
 * }
 *
 * @example
 * // With custom auth
 * import { useAuth } from './my-auth'
 *
 * function CustomUserIdProvider() {
 *   const { currentUser } = useAuth()
 *   return (
 *     <UserIdProvider
 *       getUserId={() => currentUser?.uid || null}
 *       deps={[currentUser?.uid]}
 *     />
 *   )
 * }
 */
export function UserIdProvider({ getUserId, deps = [], onUserIdChange }: UserIdProviderProps) {
  const lastUserIdRef = useRef<string | null>(null)

  useEffect(() => {
    const updateUserId = async () => {
      const userId = await getUserId()

      if (userId !== lastUserIdRef.current) {
        lastUserIdRef.current = userId
        onUserIdChange?.(userId)

        // Store userId globally and dispatch event
        if (typeof window !== 'undefined') {
          (window as any).__usecelo_userId = userId
          window.dispatchEvent(new CustomEvent('usecelo:user-changed', {
            detail: { userId }
          }))
        }
      }
    }

    updateUserId()
  }, deps) // eslint-disable-line react-hooks/exhaustive-deps

  return null
}
